import React, { useState, ReactNode, useMemo, useId } from "react";
import styles from "./index.module.css";
import { Link, Typography } from "@mui/material";
import { Application, Extension } from "@verity/proctoring-option/types";
import i18nPackage from "../../i18n/i18n";
import Checkmark from "../images/filled-checkmark.svg?react";
import { CoreRadioCardIcon, CoreTabs, EllipsisTooltip, OverflowText, TabsConfig } from "../../core";
import { PROCTORING_OPTION_TEXT } from "../proctoring-options";
import { ConfirmationEditButton } from "./ConfirmationBlock";
import {
	getEditQuizStepName,
	GROUPED_OPTION_TEXT,
	isGroupedOptionKey,
	isProctoringOptionCategory,
	ProctoringOptionCategory,
	RenderableProctoringKey,
} from "../proctoring-options/option-text/ProctoringOptionText";
import { EditQuizViewMoreButton } from "../components/EditQuizButton";
import SubOptionIcon from "../images/subOptionLine.svg?react";
import { getUniqueApplicationNamesAndIcons } from "./confirmationHelper";

export type NestedOption = {
	text: React.ReactNode;
	children?: NestedOption[];
	isList?: boolean;
};

type CollapsableRowProps = {
	children: React.ReactNode;
	icon?: React.ReactNode;
};

function CollapsableRow({ children, icon }: CollapsableRowProps) {
	return (
		<div className={styles.collapsibleListRow}>
			{icon}
			{typeof children === "string" ? <OverflowText className={styles.listRowText}>{children}</OverflowText> : children}
		</div>
	);
}

type CollapsibleListProps = {
	items: ReactNode[];
	maxVisible?: number;
	title?: React.ReactNode;
};

export const CollapsibleList = ({ items, maxVisible = 5, title }: CollapsibleListProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const visibleCount = isExpanded ? items.length : maxVisible;
	const hiddenCount = items.length - visibleCount;

	return (
		<div className={styles.collapsibleListContainer}>
			{title && <Typography className={styles.listTitle}>{title}</Typography>}
			<div className={styles.collapsibleList}>
				{items.slice(0, visibleCount)}
				{items.length > maxVisible && (
					<EditQuizViewMoreButton
						text={
							isExpanded
								? i18nPackage.t("editQuizPage.configurationSummaryListTSX.showLess")
								: i18nPackage.t("editQuizPage.configurationSummaryListTSX.showMore", { count: hiddenCount })
						}
						onClick={() => setIsExpanded(!isExpanded)}
						expanded={isExpanded}
					/>
				)}
			</div>
		</div>
	);
};

function CheckedSubOption({ children }: { children: ReactNode }) {
	return (
		<div className={styles.nestedRow}>
			<Checkmark aria-hidden={true} />
			<Typography className={styles.nestedRowText}>{children}</Typography>
		</div>
	);
}

type RenderListProps = {
	currentOptionKey: RenderableProctoringKey;
} & Pick<ConfigurationSummaryListProps, "optionChildren" | "applications" | "extensions">;

/**
 * Renders List UI
 *
 * Looks for the parent option key which has the structure:
 *
 * text: string
 * children: [array of strings]
 * isList: true
 *
 * the text is used as the list title
 */
function renderList({ currentOptionKey, optionChildren, applications, extensions }: RenderListProps) {
	const children = optionChildren.get(currentOptionKey) || [];
	if (children.length === 0) return null;

	const parentList = children.find((item) => item.children && item.children.length > 0 && item.isList);
	if (!parentList) return null;

	const listTitle = parentList.text;

	// Technically shouldn't be undefined
	const parentListChildren = parentList.children ?? [];
	const processNames = parentListChildren.map((i) => i.text as string);

	let listRows: ReactNode[] = [];

	if (currentOptionKey === "applicationBlockMode") {
		const appMap = getUniqueApplicationNamesAndIcons(processNames, applications);
		listRows = Object.entries(appMap).map(([name, icon], i) => (
			<CollapsableRow
				key={i}
				icon={icon && <div className={styles.iconWrapper} dangerouslySetInnerHTML={{ __html: icon }} />}
			>
				{name}
			</CollapsableRow>
		));
	} else if (currentOptionKey === "extensionBlockMode") {
		listRows = processNames.map((entry, i) => {
			const id = entry.slice(-32);
			const name = entry.slice(0, -32);

			// Try to find preset extension by ID
			const preset = extensions.find((ext) => ext.id === id);
			const icon = preset?.iconSvg;

			return (
				<CollapsableRow
					key={i}
					icon={icon && <div className={styles.iconWrapper} dangerouslySetInnerHTML={{ __html: icon }} />}
				>
					{preset ? preset.name : name}
				</CollapsableRow>
			);
		});
	}
	// Default (Tabs, Keyboard, etc.)
	else {
		listRows = parentListChildren.map((child, i) => {
			const content = child.text;
			return (
				<CollapsableRow key={i}>
					{currentOptionKey === "tabControl" && typeof content === "string" ? (
						<EllipsisTooltip entry={content}>
							<Link
								className={styles.listLink}
								href={content}
								target="_blank"
								aria-label={i18nPackage.t("editQuizPage.configurationSummaryListTSX.listLinkAriaLabel", {
									url: content,
								})}
							>
								{content}
							</Link>
						</EllipsisTooltip>
					) : (
						content
					)}
				</CollapsableRow>
			);
		});
	}

	return <CollapsibleList title={listTitle} items={listRows} />;
}

function renderNestedChildren(items: NestedOption[]) {
	return (
		<>
			{items.map((item, index) => (
				<div key={index} className={styles.nestedCol}>
					{<CheckedSubOption>{item.text}</CheckedSubOption>}
					{item.children && item.children.length > 0 && (
						<div className={styles.indendetedRow}>
							<SubOptionIcon />
							{renderNestedChildren(item.children)}
						</div>
					)}
				</div>
			))}
		</>
	);
}

/**
 * Renders checkmarks
 *
 * Looks for NestedChildren where isList is false.
 *
 * isList = true is handled be renderList
 *
 */
function renderOptionChildren(
	currentOptionKey: RenderableProctoringKey,
	optionChildren: ConfigurationSummaryListProps["optionChildren"],
) {
	const children = optionChildren.get(currentOptionKey);
	if (!children || children.length === 0) return null;

	const inlineItems = children.filter((item) => !item.isList);

	if (inlineItems.length === 0) return null;

	return renderNestedChildren(inlineItems);
}

export type ConfigurationSummaryListProps = {
	optionCategories: Record<ProctoringOptionCategory, RenderableProctoringKey[]>;
	optionChildren: Map<string, NestedOption[]>;
	applications: Application[];
	extensions: Extension[];
	onEdit?: (category: ProctoringOptionCategory, proctoringOption: RenderableProctoringKey) => void;
};

export default function ConfigurationSummaryList({
	optionCategories,
	optionChildren,
	applications,
	extensions,
	onEdit,
}: ConfigurationSummaryListProps) {
	const allCategoryKeys = Object.keys(optionCategories) as ProctoringOptionCategory[];

	const [curCategory, setCurCategory] = useState<ProctoringOptionCategory>(allCategoryKeys[0]);
	const baseId = useId();

	// For accessibility. Record of tabpanels and their Ids
	const tabIdRecord = useMemo((): Partial<Record<ProctoringOptionCategory, string>> => {
		const entries = allCategoryKeys.map((category) => [category, `${baseId}-${category}`] as const);
		return Object.fromEntries(entries);
	}, [allCategoryKeys, baseId]);

	const tabList = useMemo((): TabsConfig[] => {
		return allCategoryKeys.map((category) => {
			return {
				key: category,
				label: getEditQuizStepName(category),
				id: tabIdRecord[category],
			};
		});
	}, [allCategoryKeys, tabIdRecord]);

	const currentCategoryKeys = optionCategories[curCategory];

	if (allCategoryKeys.length < 1) return null;

	return (
		<div className={styles.configSummaryContainer}>
			<CoreTabs<ProctoringOptionCategory>
				tabs={tabList}
				value={curCategory}
				onChange={(_, newTab) => setCurCategory(newTab)}
				scrollable
				scrollButtons="auto"
			/>
			<div
				className={styles.configTabpanel}
				role="tabpanel"
				key={curCategory}
				aria-labelledby={tabIdRecord[curCategory]}
			>
				{currentCategoryKeys.map((optionKey, index) => {
					const optionData = isGroupedOptionKey(optionKey)
						? GROUPED_OPTION_TEXT[optionKey]
						: PROCTORING_OPTION_TEXT[optionKey];

					return (
						<div key={index} className={`${styles.confirmationContainer} ${styles.configuration}`}>
							<div className={styles.headerRow}>
								<div className={styles.headerIconWrapper}>
									{optionData.icon && <CoreRadioCardIcon enabled={true} icon={<optionData.icon />} showCheckmark />}
									<div className={styles.descriptionRow}>
										<Typography className={styles.name} variant="h3">
											{optionData.header}
										</Typography>
										<Typography className={styles.description}>{optionData.description}</Typography>
										{optionChildren?.has(optionKey) && renderOptionChildren(optionKey, optionChildren)}
									</div>
								</div>
								{onEdit && isProctoringOptionCategory(curCategory) && (
									<ConfirmationEditButton
										targetPageName={curCategory}
										onEdit={() => onEdit(curCategory, optionKey)}
										hasText={false}
									/>
								)}
							</div>
							{optionChildren?.has(optionKey) &&
								renderList({ applications, currentOptionKey: optionKey, extensions, optionChildren })}
						</div>
					);
				})}
			</div>
		</div>
	);
}
